import { EditorConfig } from "@ckeditor/ckeditor5-core";
import { ClassicEditor } from "@ckeditor/ckeditor5-editor-classic";
import {
  Autoformat,
  AutoLink,
  Autosave,
  Bold,
  Essentials,
  Highlight,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  Mention,
  Paragraph,
  Strikethrough,
  TodoList,
  Underline,
  WordCount,
  Undo,
  // AutoImage, // Available but not used yet
  // ImageInsert, // Available but not used yet
} from "./ckeditor-plugins";
import extraPlugins from "./plugins";

export const LICENSE_KEY = "GPL";

// Base plugins - always included
export const BASE_EDITOR_PLUGINS = [
  Autoformat,
  AutoLink,
  Autosave,
  Bold,
  Essentials,
  Highlight,
  
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  Mention,
  Paragraph,
  Strikethrough,
  TodoList,
  Underline,
  WordCount,
  Undo,
];

// Function to create plugin array with optional extra plugins
export const createEditorPlugins = (includeExtraPlugins: boolean = false) => {
  return includeExtraPlugins 
    ? [...BASE_EDITOR_PLUGINS, ...extraPlugins]
    : BASE_EDITOR_PLUGINS;
};

// Default plugins configuration (with extra plugins enabled by default for backward compatibility)
export const CLASSIC_EDITOR_PLUGINS = createEditorPlugins(true);

// Define default configuration as a separate object
export const CLASSIC_EDITOR_CONFIG: EditorConfig = {
  // Hide the toolbar by providing empty items array
  toolbar: {
    items: [], // Empty toolbar - effectively hidden
    shouldNotGroupWhenFull: false,
  },

  // Image configuration
  image: {
    toolbar: [],
  },

  // License key
  licenseKey: LICENSE_KEY,

  // Link configuration
  link: {
    addTargetToExternalLinks: true,
    defaultProtocol: "https://",
    decorators: {
      toggleDownloadable: {
        mode: "manual",
        label: "Downloadable",
        attributes: {
          download: "file",
        },
      },
    },
  },

  // Mention configuration
  mention: {
    feeds: [
      {
        marker: "@",
        feed: [
          /* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
        ],
      },
    ],
  },

  // Placeholder text
  placeholder: "Type or paste your content here!",

  // List configuration
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
};

// Helper: resolve element or treat string as data
function resolveSource(
  source: HTMLElement | string
): { element?: HTMLElement; data?: string } {
  if (typeof source !== "string") {
    return { element: source };
  }

  // Heuristic: if string starts with '<' treat as HTML/data
  if (source.trim().startsWith("<")) {
    return { data: source };
  }

  // Try CSS selector first
  const bySelector = document.querySelector<HTMLElement>(source);
  if (bySelector) return { element: bySelector };

  // Try id lookup (when user passed "editor" instead of "#editor")
  const byId = document.getElementById(source);
  if (byId) return { element: byId };

  // Fallback: treat as data (so at least it doesn’t crash silently)
  // but we warn so user knows the selector failed.
  if (process.env.NODE_ENV !== "production") {
    console.warn(
      `[arbo-ckeditor] Could not find element for selector/id "${source}". Treating it as initial data string.`
    );
  }
  return { data: source };
}

// Create a custom editor class WITHOUT modifying the original ClassicEditor
export default class CustomClassicEditor extends ClassicEditor {
  // Define builtin plugins for this specific editor instance (widened type to allow dynamic reassignment)
  public static override builtinPlugins: any[] = [...BASE_EDITOR_PLUGINS];

  // Define default config for this specific editor instance
  public static override defaultConfig = CLASSIC_EDITOR_CONFIG;

  // FIXED: Add a static method to create editor with proper plugin handling
  public static override async create(
    sourceElementOrData: HTMLElement | string,
    config?: EditorConfig & { includeExtraPlugins?: boolean }
  ): Promise<CustomClassicEditor> {
    const { includeExtraPlugins = true, ...cleanConfig } = config || {};

    const { element, data } = resolveSource(sourceElementOrData);

    if (!element && !data) {
      throw new Error(
        "[arbo-ckeditor] Invalid source: provide HTMLElement, selector, id, HTML markup, or data string."
      );
    }

    const pluginsToUse = createEditorPlugins(includeExtraPlugins);
    const hasCustomPlugins =
      Array.isArray(cleanConfig?.plugins) && cleanConfig.plugins.length > 0;

    let mergedConfig: EditorConfig;
    const originalBuiltin = this.builtinPlugins;

    try {
      if (hasCustomPlugins) {
        // Use plugins from config directly
        this.builtinPlugins = [];
        mergedConfig = {
          ...CLASSIC_EDITOR_CONFIG,
          ...cleanConfig,
          plugins: cleanConfig.plugins,
        };
      } else {
        // Use dynamic builtinPlugins
        this.builtinPlugins = pluginsToUse;
        mergedConfig = {
          ...CLASSIC_EDITOR_CONFIG,
          ...cleanConfig,
        };
        delete (mergedConfig as any).plugins;
      }

      const created = (await super.create(
        element ?? (data as string),
        mergedConfig
      )) as CustomClassicEditor;

      // If we passed data string (no host element), nothing to replace in DOM
      return created;
    } catch (e) {
      throw e;
    } finally {
      // Always restore builtinPlugins
      this.builtinPlugins = originalBuiltin;
    }
  }
}

// Export both the class and the configuration separately for flexibility
export {
  CustomClassicEditor,
  CLASSIC_EDITOR_CONFIG as ClassicEditorConfig,
  CLASSIC_EDITOR_PLUGINS as ClassicEditorPlugins,
};

// Also export a convenience function to create the editor
export const createClassicEditor = async (
  element: HTMLElement | string,
  customConfig?: Partial<EditorConfig> & { includeExtraPlugins?: boolean }
): Promise<CustomClassicEditor> => {
  return CustomClassicEditor.create(element, customConfig);
};
